import type { CustomDataMap, DatapointOptions } from 'owa-analytics-types';
import {
    logStartUsage,
    logStartCoreUsage,
    logStartGreyError,
    logStartCoreGreyError,
} from 'owa-analytics-start';
import type { AnalyticsEventNames, AnalyticsCoreEventNames } from 'owa-analytics-events';
import type { MailboxInfo } from 'owa-client-types';

/**
 * Specifies the type of function that will be used for logging
 */
type LogUsageFunc = (
    eventName: AnalyticsEventNames | AnalyticsCoreEventNames,
    _mailbox: MailboxInfo | undefined,
    data?: CustomDataMap,
    options?: DatapointOptions
) => void;

type LogCoreUsageFunc = (
    eventName: AnalyticsCoreEventNames,
    _mailbox: MailboxInfo | undefined,
    data?: CustomDataMap,
    options?: DatapointOptions
) => void;

type LogGreyErrorFunc = (
    eventName: AnalyticsEventNames | AnalyticsCoreEventNames,
    _mailbox: MailboxInfo | undefined,
    error?: Error,
    data?: CustomDataMap,
    options?: DatapointOptions
) => void;

type LogCoreGreyErrorFunc = (
    eventName: AnalyticsCoreEventNames,
    _mailbox: MailboxInfo | undefined,
    error?: Error,
    data?: CustomDataMap,
    options?: DatapointOptions
) => void;

let overrideLogUsage: LogUsageFunc | undefined;
let overrideLogCoreUsage: LogCoreUsageFunc | undefined;
let overrideLogGreyError: LogGreyErrorFunc | undefined;
let overrideLogCoreGreyError: LogCoreGreyErrorFunc | undefined;

export function setAccountLogUsageOverrides(
    logUsage?: LogUsageFunc,
    logGreyError?: LogGreyErrorFunc,
    logCoreUsage?: LogCoreUsageFunc,
    logCoreGreyError?: LogCoreGreyErrorFunc
): void {
    overrideLogUsage = logUsage;
    overrideLogCoreUsage = logCoreUsage;
    overrideLogGreyError = logGreyError;
    overrideLogCoreGreyError = logCoreGreyError;
}

/**
 * The account source list store is used for account management in both OOBE and the mail
 * application. To make sure the logging comes through in both OOBE and the mail/calendar
 * apps. The logUsageFromAccounts provides this support by allowing the logging method
 * to be switched based on whether the logging is happening in OOBE or not.
 * @param eventName Name of the event to log
 * @param data data associated with the event
 */
export function logUsageFromAccounts(
    eventName: AnalyticsEventNames | AnalyticsCoreEventNames,
    mailbox: MailboxInfo | undefined,
    data?: CustomDataMap,
    options?: DatapointOptions
): void {
    const logFunc = overrideLogUsage || logStartUsage;
    logFunc(eventName, mailbox, data, options);
}

/**
 * The account source list store is used for account management in both OOBE and the mail
 * application. To make sure the logging comes through in both OOBE and the mail/calendar
 * apps. The logUsageFromAccounts provides this support by allowing the logging method
 * to be switched based on whether the logging is happening in OOBE or not.
 * @param eventName Name of the event to log
 * @param data data associated with the event
 */
export function logCoreUsageFromAccounts(
    eventName: AnalyticsCoreEventNames,
    mailbox: MailboxInfo | undefined,
    data?: CustomDataMap,
    options?: DatapointOptions
): void {
    const logFunc = overrideLogCoreUsage || logStartCoreUsage;
    logFunc(eventName, mailbox, data, options);
}

/**
 * The account source list store is used for account management in both OOBE and the mail
 * application. To make sure the logging comes through in both OOBE and the mail/calendar
 * apps. The logGreyErrorFromAccounts provides this support by allowing the logging method
 * to be switched based on whether the logging is happening in OOBE or not.
 * @param eventName Name of the event to log
 * @param data data associated with the event
 */
export function logGreyErrorFromAccounts(
    eventName: AnalyticsEventNames | AnalyticsCoreEventNames,
    mailbox: MailboxInfo | undefined,
    error?: Error,
    data?: CustomDataMap,
    options?: DatapointOptions
): void {
    const logFunc = overrideLogGreyError || logStartGreyError;
    logFunc(eventName, mailbox, error, data, options);
}
/**
 * The account source list store is used for account management in both OOBE and the mail
 * application. To make sure the logging comes through in both OOBE and the mail/calendar
 * apps. The logGreyErrorFromAccounts provides this support by allowing the logging method
 * to be switched based on whether the logging is happening in OOBE or not.
 * @param eventName Name of the event to log
 * @param data data associated with the event
 */
export function logCoreGreyErrorFromAccounts(
    eventName: AnalyticsCoreEventNames,
    mailbox: MailboxInfo | undefined,
    error?: Error,
    data?: CustomDataMap,
    options?: DatapointOptions
): void {
    const logFunc = overrideLogCoreGreyError || logStartCoreGreyError;
    logFunc(eventName, mailbox, error, data, options);
}
